import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { DocumentActions } from "@/pages/legal/Agreements";

const STATUSES = ["APPLIED", "UNDER_REVIEW", "APPROVED", "REJECTED", "EXPIRED"];

export default function StatutoryApprovals() {
  const { companies } = useCompany() || {};
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: approvals }, { data: projectList }] = await Promise.all([api.get("/legal/statutory-approvals"), api.get("/projects")]);
      setRows(approvals);
      setProjects(projectList);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const approvedCount = rows.filter((r) => r.status === "APPROVED").length;
  const approvalPct = rows.length ? Math.round((approvedCount / rows.length) * 100) : 0;

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/legal/alerts/scan");
      toast.success(`Scan complete — ${data.open_total} open expiry/hearing alert(s)`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="statutory-approvals-page">
      <PageHeader
        title="Statutory Approvals & Licences"
        description={`Authority, reference, validity and status per licence. ${approvalPct}% approved (${approvedCount}/${rows.length}).`}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={scan} disabled={scanning} data-testid="scan-legal-alerts-button">{scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Scan for expiries</Button>
            <CreateApprovalDialog companies={companies} projects={projects} onCreated={load} />
          </div>
        }
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && rows.length === 0 ? (
          <EmptyState title="No statutory approvals yet" testId="statutory-approvals-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Authority</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead><TableHead>Applied</TableHead><TableHead>Valid Until</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`statutory-approval-row-${r.id}`}>
                  <TableCell className="text-foreground">{r.authority}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.approval_type}</TableCell>
                  <TableCell className="font-mono text-xs">{r.reference_no || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(r.applied_date)}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(r.valid_until)}</TableCell>
                  <TableCell><StatusSelect approval={r} onChanged={load} /></TableCell>
                  <TableCell className="text-right"><DocumentActions entity={r} kind="statutory-approvals" onUploaded={load} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function StatusSelect({ approval, onChanged }) {
  const update = async (status) => {
    try {
      await api.patch(`/legal/statutory-approvals/${approval.id}`, { status });
      toast.success("Status updated");
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  return (
    <Select value={approval.status} onValueChange={update}>
      <SelectTrigger className="h-7 w-36" data-testid={`statutory-approval-status-select-${approval.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function CreateApprovalDialog({ companies, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", project_id: "", authority: "", approval_type: "", reference_no: "", applied_date: todayISO(), valid_until: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/legal/statutory-approvals", { ...form, project_id: form.project_id || null, valid_until: form.valid_until || null });
      toast.success("Statutory approval created");
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-statutory-approval-button"><Plus className="h-4 w-4" /> New Approval</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Statutory Approval / Licence</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-statutory-approval-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Authority</Label><Input required value={form.authority} onChange={set("authority")} className="mt-1.5" data-testid="approval-authority-input" /></div>
            <div><Label>Approval type</Label><Input required value={form.approval_type} onChange={set("approval_type")} className="mt-1.5" data-testid="approval-type-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="approval-company-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reference no.</Label><Input value={form.reference_no} onChange={set("reference_no")} className="mt-1.5" data-testid="approval-reference-input" /></div>
          </div>
          <div>
            <Label>Project (optional)</Label>
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="approval-project-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">Not linked</SelectItem>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Applied date</Label><Input required type="date" value={form.applied_date} onChange={set("applied_date")} className="mt-1.5" data-testid="approval-applied-date-input" /></div>
            <div><Label>Valid until</Label><Input type="date" value={form.valid_until} onChange={set("valid_until")} className="mt-1.5" data-testid="approval-valid-until-input" /></div>
          </div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="create-statutory-approval-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
